<template>
    <div>
        <v-row align="center" class="mb-1">
            <v-col cols="10"> Threshold Config </v-col>
            <v-col class="d-flex justify-end align-center" cols="2">
                <v-btn
                    small
                    color="blue-grey"
                    class="mr-2"
                    @click="getThresholdConfigData"
                    :loading="loading"
                    ><v-icon>mdi-refresh</v-icon></v-btn
                >
                <v-btn
                    small
                    color="primary"
                    class="mr-3"
                    @click="() => showDialog('add')"
                    :loading="loading"
                    :disabled="
                        thresholdData.length === bookList.length ? true : false
                    "
                >
                    {{ $t("common.form.add") }}
                </v-btn>
            </v-col>
        </v-row>
        <v-data-table
            :items-per-page="-1"
            :headers="computedHeader"
            :header-props="{ sortIcon: null }"
            :items="thresholdData"
            :loading="loading"
            hide-default-footer
            dense
        >
            <template v-slot:item.actions="{ item }">
                <v-icon
                    class="mr-2"
                    @click="() => showDialog('edit', item)"
                    :disabled="loading"
                >
                    mdi-pencil
                </v-icon>
                <v-menu :close-on-content-click="false" offset-y top>
                    <template v-slot:activator="{ on }">
                        <v-icon dense :disabled="loading" v-on="on"
                            >mdi-delete</v-icon
                        >
                    </template>
                    <v-card class="pa-3" width="220">
                        <div class="text-body-2 mb-3">
                            Confirm to delete this configuration?
                        </div>
                        <div class="d-flex justify-end">
                            <v-btn text small class="mr-1">
                                {{ $t("common.form.cancel") }}
                            </v-btn>
                            <v-btn
                                color="error"
                                small
                                :loading="loading"
                                @click="deleteConfig(item)"
                            >
                                {{ $t("common.form.confirm") }}
                            </v-btn>
                        </div>
                    </v-card>
                </v-menu>
            </template>
        </v-data-table>
        <v-dialog v-model="dialog" width="800" persistent>
            <v-card>
                <v-app-bar
                    flat
                    color="rgba(0, 0, 0, 0)"
                    style="background: rgb(26, 57, 88)"
                >
                    <v-toolbar-title class="text-h6 white--text pl-0">
                        {{
                            editingMode !== "add"
                                ? $t("common.form.edit")
                                : $t("common.form.add")
                        }}
                        {{ this.selectedTab }} Threshold Configuration
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn color="white" icon @click="dialog = false"
                        ><v-icon>mdi-close</v-icon></v-btn
                    >
                </v-app-bar>

                <v-container class="px-6 pt-5 pb-2" v-if="editingItem">
                    <v-row align="center" class="mb-1">
                        <v-col cols="4">
                            <span class="text-body-2 text--darken-2">
                                Book
                            </span>
                        </v-col>
                        <v-col cols="8">
                            <v-select
                                v-model="editingItem['book']"
                                :items="bookList"
                                dense
                                outlined
                                hide-details
                                :readonly="editingMode === 'update'"
                                :disabled="editingMode === 'update'"
                            >
                                <template v-slot:selection="{ item }">
                                    <v-chip x-small>{{ item }}</v-chip>
                                </template>
                                <template v-slot:item="{ item, attrs, on }">
                                    <v-list-item
                                        v-bind="attrs"
                                        v-on="on"
                                        :disabled="takenBooks.includes(item)"
                                    >
                                        <v-list-item-content>
                                            <v-list-item-title
                                                :class="
                                                    takenBooks.includes(item)
                                                        ? 'grey--text'
                                                        : ''
                                                "
                                            >
                                                {{ item }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                        <v-list-item-action
                                            v-if="takenBooks.includes(item)"
                                        >
                                            <v-chip
                                                x-small
                                                color="grey"
                                                text-color="white"
                                                >configured</v-chip
                                            >
                                        </v-list-item-action>
                                    </v-list-item>
                                </template>
                            </v-select>
                        </v-col>
                        <template
                            v-for="field in tableMappingFields[selectedTab]"
                        >
                            <v-col cols="4">
                                <span class="text-body-2 text--darken-2">
                                    {{
                                        capitalizeKey(
                                            field
                                                .replace(
                                                    "volume_usd",
                                                    "std_lot"
                                                )
                                                .replace("usd", "USD"),
                                            "_",
                                            " "
                                        )
                                    }}
                                </span>
                            </v-col>
                            <v-col cols="8">
                                <div class="d-flex align-center">
                                    <v-text-field
                                        v-model="editingItem['l1_' + field]"
                                        label="L1"
                                        dense
                                        outlined
                                        hide-details
                                        class="mr-2"
                                        :error="
                                            editingItem &&
                                            editingItem['l1_' + field] !==
                                                undefined &&
                                            (editingItem['l1_' + field] ==
                                                null ||
                                                editingItem['l1_' + field] ===
                                                    '' ||
                                                isNaN(
                                                    Number(
                                                        editingItem[
                                                            'l1_' + field
                                                        ]
                                                    )
                                                ))
                                        "
                                        @keydown="onlyNumber"
                                        @change="
                                            (val) =>
                                                normalizeLeadingZero(
                                                    field,
                                                    'l1',
                                                    val
                                                )
                                        "
                                    />
                                    <v-text-field
                                        v-model="editingItem['l2_' + field]"
                                        label="L2"
                                        dense
                                        outlined
                                        hide-details
                                        style="width: 150px"
                                        class="mr-3"
                                        :error="
                                            editingItem &&
                                            editingItem['l2_' + field] !==
                                                undefined &&
                                            (editingItem['l2_' + field] ==
                                                null ||
                                                editingItem['l2_' + field] ===
                                                    '' ||
                                                isNaN(
                                                    Number(
                                                        editingItem[
                                                            'l2_' + field
                                                        ]
                                                    )
                                                ) ||
                                                (selectedTab === 'Fast Trade'
                                                    ? Number(
                                                          editingItem[
                                                              'l2_' + field
                                                          ]
                                                      ) >
                                                      Number(
                                                          editingItem[
                                                              'l1_' + field
                                                          ]
                                                      )
                                                    : Number(
                                                          editingItem[
                                                              'l2_' + field
                                                          ]
                                                      ) >=
                                                      Number(
                                                          editingItem[
                                                              'l1_' + field
                                                          ]
                                                      )))
                                        "
                                        @keydown="onlyNumber"
                                        @change="
                                            (val) =>
                                                normalizeLeadingZero(
                                                    field,
                                                    'l2',
                                                    val
                                                )
                                        "
                                    />
                                </div>
                            </v-col>
                        </template>
                    </v-row>
                </v-container>

                <v-card-actions class="px-6 py-3">
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        small
                        @click="dialog = false"
                        :loading="loading"
                    >
                        {{ $t("common.form.cancel") }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        small
                        @click="submit"
                        :loading="loading"
                        :disabled="!isFormValid"
                    >
                        {{ $t("common.form.submit") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { snackbar } from "@components/mixins/snackbar";
import { permission } from "@components/mixins/permission";
import { createCancelToken } from "@utils/request";
import {
    getAlertHubThreshold,
    updateAlertHubThreshold,
    deleteThresholdConfig,
} from "@services/alert/config";
import deepClone from "deep-clone";
import { helper } from "@components/mixins/helper";

export default {
    mixins: [permission, snackbar, helper],
    props: {
        selectedTab: "",
    },
    data() {
        return {
            typeTableNameMapping: {
                MSG_TYPE_MONITOR_FAST_CLOSED_TRADE: "Fast Trade",
                MSG_TYPE_MONITOR_HIGH_VOLUME_USD: "High Volume USD",
                MSG_TYPE_MONITOR_HIGH_TRADE_COUNT: "High Trades",
                MSG_TYPE_MONITOR_TODAY_OPENED_LARGE_PNL: "Large PNL",
                MSG_TYPE_MONITOR_LOW_LIQUIDITY_ARBITRAGE:
                    "Low Liquidity Arbitrage",
            },
            tableMappingFields: {
                "Fast Trade": ["pnl_threshold", "trade_count_threshold"],
                "High Volume USD": ["volume_usd_threshold"],
                "High Trades": ["trade_count_threshold"],
                "Large PNL": ["pnl_usd_threshold"],
                "Low Liquidity Arbitrage": ["pnl_threshold"],
            },
            loading: false,
            thresholdData: [],
            cancelTokenSource: null,
            dialog: false,
            editingMode: undefined,
            editingItem: null,
        };
    },
    computed: {
        ...mapState("SettingsBooks", ["bookData"]),
        computedHeader() {
            const header = [
                {
                    text: "Book",
                    value: "book",
                },
            ];
            switch (this.selectedTab) {
                default:
                    for (const level of ["l1", "l2"]) {
                        for (const field of this.tableMappingFields[
                            this.selectedTab
                        ] || []) {
                            header.push({
                                text: this.capitalizeKey(
                                    level +
                                        "_" +
                                        field
                                            .replace("volume_usd", "std_lot")
                                            .replace("usd", "USD"),
                                    "_",
                                    " "
                                ),
                                value: level + "_" + field,
                                sortable: false,
                            });
                        }
                    }
                    header.push({
                        text: this.$t("common.table.actions"),
                        value: "actions",
                        sortable: false,
                    });
                    return header;
            }
        },
        msgType() {
            if (this.selectedTab) {
                return Object.keys(this.typeTableNameMapping).find((key) => {
                    return this.typeTableNameMapping[key] === this.selectedTab;
                });
            }
            return undefined;
        },
        bookList() {
            return this.bookData.map((book) =>
                book.book_name === "All" ? "*" : book.book_name
            );
        },
        takenBooks() {
            return this.thresholdData.map((row) => row.book);
        },
        isFormValid() {
            if (!this.editingItem || !this.editingItem.book) return false;
            const fields = this.tableMappingFields[this.selectedTab] || [];
            const isFastTrade = this.selectedTab === "Fast Trade";
            return fields.every((field) => {
                const l1 = this.editingItem["l1_" + field];
                const l2 = this.editingItem["l2_" + field];
                const basicValid = [l1, l2].every(
                    (val) =>
                        val !== null &&
                        val !== undefined &&
                        val !== "" &&
                        !isNaN(Number(val))
                );
                if (!basicValid) return false;
                return isFastTrade
                    ? Number(l2) <= Number(l1)
                    : Number(l2) < Number(l1);
            });
        },
    },
    watch: {
        selectedTab(nv) {
            if (nv) {
                this.thresholdData = [];
                this.getThresholdConfigData();
            }
        },
    },
    methods: {
        onlyNumber(e) {
            const controlKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
                "Home",
                "End",
            ];
            if (controlKeys.includes(e.key)) return;
            if (!/[\d.\-]/.test(e.key)) e.preventDefault();
            if (e.key === "." && e.target.value.includes("."))
                e.preventDefault();
            if (
                e.key === "-" &&
                (e.target.selectionStart !== 0 ||
                    e.target.value.includes("-"))
            )
                e.preventDefault();
        },
        normalizeLeadingZero(field, level, val) {
            const key = level + "_" + field;
            const str = String(val);
            if (str === "0.") {
                this.editingItem[key] = 0;
            } else if (
                str.length > 1 &&
                str.startsWith("0") &&
                !str.startsWith("0.")
            ) {
                this.editingItem[key] = Number(str);
            }
        },
        getThresholdConfigData() {
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel(
                    this.$t("message.operationCanceled")
                );
            }

            this.cancelTokenSource = createCancelToken();
            this.loading = true;

            getAlertHubThreshold(
                {
                    alert_type: this.msgType,
                },
                this.cancelTokenSource
            )
                .then((res) => {
                    const result = [];
                    const data = res.data;
                    const l1Data = data?.["L1"];
                    const l2Data = data?.["L2"];
                    const l1Books = Object.keys(l1Data || {});
                    const l2Books = Object.keys(l2Data || {});
                    const uniqueBooks = Array.from(
                        new Set([...l1Books, ...l2Books])
                    );
                    const fields = Object.keys(l1Data?.[l1Books?.[0]] || {});
                    switch (this.selectedTab) {
                        default:
                            uniqueBooks.forEach((book_name) => {
                                const info = {
                                    book: book_name,
                                };
                                fields.forEach((field) => {
                                    info["l1_" + field] =
                                        l1Data[book_name][field];
                                    info["l2_" + field] =
                                        l2Data[book_name][field];
                                });
                                result.push(info);
                            });
                            this.thresholdData = result;
                            break;
                    }
                })
                .finally(() => {
                    this.loading = false;
                    this.dialog = false;
                });
        },
        showDialog(mode, item) {
            this.editingItem = null;
            this.dialog = true;
            if (mode === "add") {
                this.editingMode = "add";
                const editingItem = {
                    book: null,
                };
                for (const field of this.tableMappingFields[this.selectedTab]) {
                    editingItem["l1_" + field] = undefined;
                    editingItem["l2_" + field] = undefined;
                }
                this.editingItem = editingItem;
            } else {
                this.editingMode = "update";
                this.editingItem = deepClone(item);
            }
        },
        submit() {
            const params = {
                alert_type: this.msgType,
                book: this.editingItem?.book,
                L1: {},
                L2: {},
            };
            for (const field of this.tableMappingFields[this.selectedTab]) {
                params.L1[field] = Number(this.editingItem["l1_" + field]);
                params.L2[field] = Number(this.editingItem["l2_" + field]);
            }
            this.loading = true;
            updateAlertHubThreshold(params)
                .then(({ data }) => {
                    this.snackBarSuccess(data?.msg);
                    this.getThresholdConfigData();
                })
                .catch((e) => {
                    this.snackBarDanger(e?.response?.data?.msg);
                });
        },
        deleteConfig(item) {
            const params = {
                alert_type: this.msgType,
                book: item.book,
            };
            this.loading = true;
            deleteThresholdConfig(params)
                .then(({ data }) => {
                    this.snackBarSuccess(data?.msg);
                    this.getThresholdConfigData();
                })
                .catch((e) => {
                    this.snackBarDanger(e?.response?.data?.msg);
                });
        },
    },
};
</script>
